import { DropDownDirectionEnum, DropDownVerticalDirectionEnum } from '@alliance/santa/drop-down'
import { RxStateComponent } from '@alliance/shared/models'
import { isObjectWithProperty } from '@alliance/shared/utils'
import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { PolymorpheusContent } from '@alliance/shared/polymorpheus'
import { filter, take } from 'rxjs/operators'
import { SelectItem } from './select-item.model'

@Component({
  selector: 'santa-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.tw.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SantaSelectComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SantaSelectComponent<T>
  extends RxStateComponent<{
    items: Array<SelectItem<T>>
    selectedItem: SelectItem<T> | null
    opened: boolean
  }>
  implements OnInit, ControlValueAccessor
{
  @Input() public selectedValueContent: PolymorpheusContent | null = null
  @Input() public itemContent: PolymorpheusContent | null = null

  @Input() public label: string | undefined
  @Input() public itemUniqueKey: string | undefined
  @Input() public withoutPreselected = false
  @Input() public openWithOneItem = true
  @Input() public isRequired = false
  @Input() public invalid = false

  @Input() public showArrow = true
  @Input() public enableClosedBorder = true
  @Input() public enableOpenedDivider = true
  @Input() public hideDivider = false
  @Input() public contentFullWidth = false
  @Input() public dropDownDirection = DropDownDirectionEnum.fullSize
  @Input() public dropDownVerticalDirection = DropDownVerticalDirectionEnum.bottom
  @Input() public dropDownMaxHeight = 'auto'

  @Output() public itemSelected = new EventEmitter<T>()
  @Output() public openedChanges = new EventEmitter<boolean>()

  public readonly verticalDirectionConfig: Record<DropDownVerticalDirectionEnum, string> = {
    [DropDownVerticalDirectionEnum.top]: 'top-direction',
    [DropDownVerticalDirectionEnum.bottom]: 'bottom-direction'
  }

  public readonly state = this.connectViewState(['opened', 'items', 'selectedItem'])

  public constructor() {
    super()

    this.initState({
      opened: false,
      items: [],
      selectedItem: null
    })

    this.hold(this.select('opened'), opened => this.openedChanges.emit(opened))
    this.hold(this.select('selectedItem').pipe(filter((item): item is SelectItem<T> => item !== null)), selectedItem => {
      this.onChange(selectedItem.$implicit)
      this.resetItemsActiveState(selectedItem)
    })
  }

  @Input()
  public set preselectedItem(item: T | null) {
    if (item) {
      this.setPreselectedItem(item)
    }
  }
  @Input()
  public set items(items: T[]) {
    this.setIndexedItems(items)
  }
  @Input()
  public set opened(isOpened: boolean) {
    this.set({ opened: isOpened })
  }

  public onChange = (val: T): T => val
  public onTouched = (): void => {
    // do nothing
  }
  public writeValue = (obj: T): void => obj && this.setPreselectedItem(obj)
  public registerOnChange = (fn: (value: T) => T): void => {
    this.onChange = fn
  }
  public registerOnTouched = (fn: () => void): void => {
    this.onTouched = fn
  }

  public override ngOnInit(): void {
    super.ngOnInit()

    if (!this.withoutPreselected && !this.get()?.selectedItem) {
      this.setPreselectedItem()
      this.itemSelected.emit(this.get().selectedItem?.$implicit)
    }
  }

  public onOpenedChanges(opened: boolean): void {
    this.set({ opened })
  }

  public onItemClick(selectItem: SelectItem<T>): void {
    this.setSelectedItem(selectItem)
  }

  public getContent(content: PolymorpheusContent | null, item: SelectItem<T>): PolymorpheusContent | null {
    if (content !== null) {
      return content
    } else if (typeof item.$implicit === 'number' || typeof item.$implicit === 'string') {
      return item.$implicit
    }
    return null
  }

  public trackByIndex(index: number): number {
    return index
  }

  private setIndexedItems(items: T[]): void {
    const itemsWithUniqueId: Array<SelectItem<T>> = items.map((item: T, index: number) => ({
      $implicit: item,
      uniqueId: !!this.itemUniqueKey && isObjectWithProperty(item, this.itemUniqueKey) ? (item[this.itemUniqueKey] as string) : index.toString()
    }))

    this.set({ items: itemsWithUniqueId })
  }

  private setSelectedItem(selectedItem: SelectItem<T>): void {
    this.set({ selectedItem })
    this.itemSelected.emit(selectedItem.$implicit)
    this.onTouched()
  }

  private setPreselectedItem(preselectedItem?: T): void {
    this.hold(
      this.select('items').pipe(
        filter(list => !!list?.length),
        take(1)
      ),
      (items: Array<SelectItem<T>>) => {
        const itemInList = (preselected: T): SelectItem<T> | undefined =>
          typeof preselected === 'number' || typeof preselected === 'string'
            ? items.find((item: SelectItem<T>) => item.$implicit === preselected)
            : items.find((item: SelectItem<T>) => !!this.itemUniqueKey && isObjectWithProperty(preselected, this.itemUniqueKey) && item.uniqueId === (preselected[this.itemUniqueKey] as string))
        const selectedItem = preselectedItem ? itemInList(preselectedItem) ?? { $implicit: preselectedItem, uniqueId: '-1' } : items[0]

        this.set({ selectedItem })
      }
    )
  }

  private resetItemsActiveState(newSelectedItem: SelectItem<T>): void {
    const newList = (this.get()?.items ?? []).map((item: SelectItem<T>) => ({
      ...item,
      active: item.uniqueId === newSelectedItem.uniqueId
    }))
    this.set({ items: newList })
  }
}
